import React from 'react'
import kebabCase from 'lodash/kebabCase'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex } from 'reflexbox/styled-components'

import media from 'media'
import theme from 'theme'

import IconDynamicArrow from '@anvilco-pkg/ui/components/Icon/IconDynamicArrow'
import IconPlay from '@anvilco-pkg/ui/components/Icon/IconPlay'
import IconGear from '@anvilco-pkg/ui/components/Icon/IconGear'
import IconDocumentTemplate from '@anvilco-pkg/ui/components/Icon/IconDocumentTemplate'
import IconWebform from '@anvilco-pkg/ui/components/Icon/IconWebform'
import IconSignature from '@anvilco-pkg/ui/components/Icon/IconSignature'
import IconList from '@anvilco-pkg/ui/components/Icon/IconList'
import IconDocumentSigned from '@anvilco-pkg/ui/components/Icon/IconDocumentSigned'
import IconDocumentFilled from '@anvilco-pkg/ui/components/Icon/IconDocumentFilled'

import {
  PostContainer,
  ArrowContainer,
  Tag,
  Title,
  Summary,
  InfoContainer,
  PostHeader,
  Content,
} from '../PostSummary'

const StyledPostHeader = styled(PostHeader)`
  min-height: initial;
`

const StyledInfoContainer = styled(InfoContainer)`
  min-height: initial;

  ${media.desktop`
    padding-bottom: 40px;
  `}
`

const StyledTitle = styled(Title)`
  margin-bottom: 10px;
`

const StyledArrowContainer = styled(ArrowContainer)`
  bottom: 20px;
`

const StyledPostContainer = styled(PostContainer)`
  && img {
    width: unset;
  }
`

const Foreword = styled(Flex).attrs({
  alignItems: 'center',
  mb: 18,
})`
  gap: 4px;
`

// TODO: use renderGraphicIcon to string method here
// groups in ui package are set, this will require `renderGraphicIcon` from:
// - features
// - product
// - attributes/nouns
//
// also make this handle pluralized cases: e.g. 'tutorials' should be able to find
// `GraphicIconTutorial` or `GraphicIconTutorials`, whichever is correct
export const getImage = (tag) => {
  switch (tag.toLowerCase()) {
    case 'pdf editor':
      return (
        <IconDocumentFilled
          width={20}
          height={20}
          fill={theme.colors.blues[40]}
        />
      )
    case 'webform editor':
      return <IconList width={20} height={20} fill={theme.colors.greens[50]} />
    case 'workflows':
      return (
        <IconWebform width={20} height={20} fill={theme.colors.greens[50]} />
      )
    case 'signer editor':
      return (
        <IconDocumentSigned
          width={20}
          height={20}
          fill={theme.colors.yellows[60]}
        />
      )
    case 'document templates':
      return (
        <IconDocumentTemplate
          width={20}
          height={20}
          fill={theme.colors.blues[40]}
        />
      )
    case 'org settings':
      return <IconGear width={20} height={20} fill={theme.colors.blacks[50]} />
    case 'etch e-sign':
      return (
        <IconSignature width={20} height={20} fill={theme.colors.yellows[60]} />
      )
    case 'tutorials':
    case 'definitions':
      return <IconPlay width={20} height={20} fill={theme.colors.red} />
    default:
      return null
  }
}

const renderPostContent = ({ title, summary, tagline, tag }) => {
  return (
    <StyledInfoContainer>
      <StyledPostHeader>
        <Foreword>
          {getImage(tag)}
          <Tag>{tag}</Tag>
        </Foreword>
        <StyledTitle>{title}</StyledTitle>
      </StyledPostHeader>
      <Content>
        <Summary>{tagline || summary}</Summary>
      </Content>
    </StyledInfoContainer>
  )
}

const HelpSummary = ({
  slug,
  title,
  summary,
  tagline,
  tags,
  showDate,
  basePath,
  selectedTag,
  isRelatedArticle,
  ...others
}) => {
  // TODO: realllly need to genericize/variablize 'All'...
  // first one is 'primary tag'
  const tag = selectedTag === 'All' || isRelatedArticle ? tags[0] : selectedTag

  return (
    <StyledPostContainer to={`${basePath}/${kebabCase(tag)}/${slug}`}>
      {renderPostContent({ title, summary, tagline, tag })}
      <StyledArrowContainer>
        <IconDynamicArrow direction="right" width={50} />
      </StyledArrowContainer>
    </StyledPostContainer>
  )
}

HelpSummary.propTypes = {
  post: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    image: PropTypes.object,
    summary: PropTypes.string,
    tag: PropTypes.string,
    tagline: PropTypes.string,
    tags: PropTypes.array,
  }),
  showDate: PropTypes.bool,
  basePath: PropTypes.string,
  selectedTag: PropTypes.string,
}

export default HelpSummary
